"use client";

import { useCallback, useState } from "react";

import { LocalizedTimeText } from "@/components/ui/localized-time-text";
import { PdfFormInput } from "@/components/ui/pdf-form-input";
import { usePdfFileCache } from "@/common/hooks/use-pdf-file-cache";
import { trpc } from "@/common/utils/trpc";
import { navigateBack } from "@/common/utils/navigateBack";
import { ROUTES } from "@/setup/frontend/routes";
import {
	Box,
	Button,
	Card,
	Container,
	Flex,
	Group,
	Loader,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
	IconAlertCircle,
	IconArrowLeft,
	IconRefresh,
} from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router";
import { useOs } from "@mantine/hooks";

const emptyObject = {};

export default function CarePlanPage() {
	const { care_plan_id } = useParams<{ care_plan_id: string }>();
	const carePlanId = Number(care_plan_id);
	const navigate = useNavigate();
	const os = useOs();
	const isAndroid = os === "android";
	const [useFallbackMode, setUseFallbackMode] = useState(false);
	const [inlinePdfUrl, setInlinePdfUrl] = useState<string | null>(null);
	const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

	const {
		data: pdfDetails,
		isLoading,
		error,
	} = trpc.carePlans.getCarePlanPDFDetails.useQuery({
		care_plan_id: carePlanId,
	});
	const { data: pdfFile } = trpc.carePlans.getCarePlanPDFFile.useQuery({
		care_plan_id: carePlanId,
	});

	const generatePdfMutation = trpc.carePlans.generateAndDownloadPDF.useMutation(
		{
			onSuccess: (data) => {
				setInlinePdfUrl(data.dataUrl);
				setIsGeneratingPdf(false);
			},
			onError: (error) => {
				notifications.show({
					title: "Error",
					message: error.message || "Failed to generate PDF",
					color: "red",
				});
				setIsGeneratingPdf(false);
			},
		},
	);

	const handleGeneratePdf = useCallback(() => {
		setIsGeneratingPdf(true);
		generatePdfMutation.mutate({ care_plan_id: carePlanId });
	}, [carePlanId, generatePdfMutation]);

	const { file: cachedFile, isLoading: pdfLoading } = usePdfFileCache({
		downloadUrl: pdfFile?.downloadUrl,
		uploadedAt: pdfFile?.pdf_uploaded_at,
		idPrefix: `care-plan-${carePlanId}`,
	});

	const handleUseFallbackMode = useCallback(() => {
		setUseFallbackMode(true);
		// Generate the PDF for inline viewing
		handleGeneratePdf();
	}, [handleGeneratePdf]);

	if (isLoading) {
		return (
			<Flex direction="column" h="100vh">
				<Box style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
					<Group h={64} px="md" justify="space-between">
						<Title order={3} style={{ flex: 1, textAlign: "center" }}>
							Care Plan
						</Title>
						<Box w={48} />
					</Group>
				</Box>
				<Flex align="center" justify="center" style={{ flex: 1 }}>
					<Stack align="center" gap="md">
						<Loader size="lg" color="blue" />
						<Text size="lg" c="dimmed" fw={500}>
							Loading details...
						</Text>
					</Stack>
				</Flex>
			</Flex>
		);
	}

	if (!pdfDetails || error) {
		return (
			<Container p="md">
				<Card withBorder>
					<Stack align="center" gap="md" p="md">
						<Text size="lg" fw={500}>
							Error loading care plan details
						</Text>
						{error && (
							<Text size="sm" c="red">
								{error.message}
							</Text>
						)}
						<Button onClick={() => navigate(ROUTES.CAREGIVER_APP.ROOT)}>
							Return to Home
						</Button>
					</Stack>
				</Card>
			</Container>
		);
	}

	return (
		<Flex direction="column" flex={1} style={{ overflow: "hidden" }} w="100%">
			<Box style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
				<Group h={80} px="md" justify="space-between">
					<Group gap="xs">
						<Button
							variant="subtle"
							size="lg"
							onClick={() => navigateBack(navigate, ROUTES.CAREGIVER_APP.ROOT)}
						>
							<IconArrowLeft />
							<Text display="none">Back</Text>
						</Button>
					</Group>
					<Stack gap={4} align="center" style={{ flex: 1 }}>
						<Title order={3}>Care Plan</Title>
						<Text size="sm" c="dimmed">
							{pdfDetails.carePlan.client.first_name}{" "}
							{pdfDetails.carePlan.client.last_name} |{" "}
							<LocalizedTimeText
								value={pdfDetails.carePlan.due_at}
								format="MM/DD/YY"
							/>{" "}
							-{" "}
							<LocalizedTimeText
								value={pdfDetails.carePlan.computed?.expires_at}
								format="MM/DD/YY"
							/>
						</Text>
					</Stack>
					<Box w={48} />
				</Group>
			</Box>

			<Stack gap="md" p="md" style={{ overflow: "hidden", flex: 1 }}>
				{!useFallbackMode ? (
					<>
						<PdfFormInput
							documentKey={`care-plan-${carePlanId}`}
							file={pdfLoading ? undefined : cachedFile}
							value={pdfDetails.field_data || emptyObject}
							readOnly={true}
							defaultScale={1}
							visiblePageRange={pdfDetails.visiblePageRange}
							signatureFieldNames={pdfDetails.signatureFieldNames}
							onChange={() => {}}
						/>
						<Button
							leftSection={<IconAlertCircle size={16} />}
							variant="outline"
							color="orange"
							onClick={handleUseFallbackMode}
							disabled={!pdfFile?.downloadUrl || isAndroid}
							style={{ display: isAndroid ? "none" : undefined }}
						>
							PDF isn't loading? Tap here for alternative view
						</Button>
					</>
				) : (
					<Flex flex={1} gap="md" direction="column">
						{isGeneratingPdf ? (
							<Flex
								align="center"
								justify="center"
								direction="column"
								gap="md"
								py="xl"
							>
								<Loader size="md" />
								<Text c="dimmed">Generating PDF...</Text>
							</Flex>
						) : inlinePdfUrl ? (
							<Flex flex={1}>
								<iframe
									src={inlinePdfUrl}
									style={{ width: "100%", height: "100%", border: "none" }}
									title="Care Plan PDF"
								/>
							</Flex>
						) : (
							<Stack align="center" gap="md">
								<Text c="dimmed">
									Failed to generate the PDF. Please try again.
								</Text>
								<Button
									onClick={handleGeneratePdf}
									leftSection={<IconRefresh size={16} />}
								>
									Try Again
								</Button>
							</Stack>
						)}

						<Button onClick={() => setUseFallbackMode(false)}>
							Return to standard view
						</Button>
					</Flex>
				)}
			</Stack>
		</Flex>
	);
}
